import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '@lib/auth/data-access';
import { FeatureModel, FeatureTenantModel, FeatureToggleType } from '@lib/feature-toggle/models';
import { EnvironmentService } from '@lib/shared/data-access';
import { catchError, firstValueFrom, Observable, of, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class FeatureToggleService {

    // signals
    private tenantFeaturesSourceSignal = signal<FeatureTenantModel[]>([]);
    public tenantFeaturesComputedSignal = computed(() => { return this.tenantFeaturesSourceSignal() });

    /**
     * Whether the toggle list has been fetched yet — SETTLED, not successful.
     *
     * `tenantFeaturesComputedSignal()` is `[]` both before the fetch and when the tenant genuinely has no
     * features enabled, and nothing could tell those two apart. That ambiguity is harmless for a menu item
     * (it appears a moment late) and is not harmless for a route decision: a guard reading "no features" on a
     * cold deep link would send an officer to the legacy stack while V2 was on for their tenant, and the
     * answer would then be wrong for the whole navigation rather than late.
     *
     * SET ON THE ERROR PATH TOO. A guard that waits for this must not wait forever because the auth API is
     * down — the empty list IS the answer then, and it means "no features", which is the safe reading.
     */
    private tenantFeaturesLoadedSourceSignal = signal(false);
    public tenantFeaturesLoadedComputedSignal = this.tenantFeaturesLoadedSourceSignal.asReadonly();

    /**
     * Whether the last fetch FAILED, so the empty list means "nothing answered" rather than "no features".
     *
     * Loaded-and-empty is the safe reading for a route or a menu item. It is not for something that HIDES WORK:
     * the V2 charging rail hides a section when its feature is off, and reading a failed fetch as "off" would take
     * Bill of Costs away from a tenant that has it, and turn an officer out of the section they were in, for as
     * long as the auth API is unreachable.
     */
    private tenantFeaturesFailedSourceSignal = signal(false);
    public tenantFeaturesFailedComputedSignal = this.tenantFeaturesFailedSourceSignal.asReadonly();

    private httpClient = inject(HttpClient);
    private environmentService = inject(EnvironmentService);
    public authService = inject(AuthService);

    // Add your service methods here
    public getFeatures(): Observable<FeatureModel[]> {
        return this.httpClient.get<FeatureModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles`);
    }

    public saveFeature(feature: FeatureModel): Observable<FeatureModel> {
        return this.httpClient.put<FeatureModel>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/${feature.id}`, feature);
    }

    public saveTenantFeature(tenantFeature: FeatureTenantModel): Observable<FeatureTenantModel> {
        return this.httpClient.put<FeatureTenantModel>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/tenants/${tenantFeature.tenantId}/tenant-feature/${tenantFeature.id}`, tenantFeature);
    }   

    public getTenantFeatures(tenantExternalId: string): Observable<FeatureTenantModel[]> {
        return this.httpClient.get<FeatureTenantModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${tenantExternalId}`);
    }

    public isFeatureEnabledServerCheck(featureToggleType: FeatureToggleType): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${this.authService.userPrincipalComputedSignal()?.currentTenantExternalId}/type/${featureToggleType}/status`);
    }

    public getLoggedUserEnabledFeatures(tenantExternalId: string): Observable<FeatureTenantModel[]> {
        if (window.location.href.includes('/super-admin') && this.authService.isCodyAdministratorComputedSignal()) {
            // A settled answer, so anything waiting on the load is released rather than left hanging on a
            // request that is never going to be made.
            this.tenantFeaturesLoadedSourceSignal.set(true);
            return of([]);
        }
        return this.httpClient.get<FeatureTenantModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${tenantExternalId}/logged-user`).pipe(
            tap((tenantFeatures) => {
                this.tenantFeaturesSourceSignal.set(tenantFeatures);
                this.tenantFeaturesFailedSourceSignal.set(false);
                this.tenantFeaturesLoadedSourceSignal.set(true);
            }),
            catchError(() => {
                // Handle the error by setting an empty array to indicate no data or an error state
                this.tenantFeaturesSourceSignal.set([]);
                this.tenantFeaturesFailedSourceSignal.set(true);
                // Settled, not successful — see tenantFeaturesLoadedComputedSignal.
                this.tenantFeaturesLoadedSourceSignal.set(true);
                // Return an observable with an empty array to continue the observable chain
                return of([]);
            })
        );
    }

    /** One in-flight load, so simultaneous callers share a request rather than each starting one. */
    private inFlightLoad: Promise<void> | null = null;

    /**
     * Make sure the tenant's toggles are loaded, fetching them if nobody has.
     *
     * WHY A CALLER MIGHT HAVE TO ASK. The only thing that loaded these was `AppMenuComponent`, which lives
     * inside the app shell — so anything that runs BEFORE the shell exists could not simply wait for them.
     * A route guard is exactly that: it decides whether a route matches, which happens before any component
     * of that route tree is created. Waiting there deadlocked outright — the guard waited for the toggles,
     * and the toggles waited for a component the guard was blocking — and a hard refresh on a gated URL
     * rendered a blank page while back and forward worked, because those reused a shell that already existed.
     *
     * Idempotent and shared: already loaded resolves immediately, and concurrent callers get the same
     * promise. It never rejects — `getLoggedUserEnabledFeatures` swallows its own failure into an empty list,
     * which is the safe reading and keeps a caller from hanging on a down auth API.
     */
    public ensureTenantFeaturesLoaded(tenantExternalId: string): Promise<void> {
        if (this.tenantFeaturesLoadedSourceSignal()) {
            return Promise.resolve();
        }

        this.inFlightLoad ??= firstValueFrom(this.getLoggedUserEnabledFeatures(tenantExternalId))
            .then(() => undefined)
            .finally(() => { this.inFlightLoad = null; });

        return this.inFlightLoad;
    }

    public isFeatureEnabled(featureType: FeatureToggleType): boolean {
        return this.tenantFeaturesComputedSignal().some(feature => feature.id === featureType && feature.isEnabled);
    }
}
